/**
 * Gmail-style two-key navigation sequence: press `g`, then a destination key
 * (e.g. `g` then `t` → Tickets). Kept as a pure state machine with an explicit
 * `now` timestamp so it is fully unit-testable without the DOM or timers.
 *
 * This exists because navigation used to be registered as `g+d`, `g+t`, … which
 * `parseShortcut` collapsed to the bare single keys `d`, `t`, … — so a stray
 * keystroke matching a sidebar first-letter navigated away. Requiring the `g`
 * prefix restores the intended sequence and kills the accidental single-key nav.
 */
export interface NavSequenceMatcher {
  /** Feed a lowercased key + timestamp; returns a target path when the sequence completes, else null. */
  press(key: string, now: number): string | null;
}

const LEADER_KEY = 'g';
const DEFAULT_TIMEOUT_MS = 1000;

export function createNavSequence(
  map: Record<string, string>,
  options: { timeoutMs?: number } = {},
): NavSequenceMatcher {
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  // Timestamp of the last `g` press; null means "not armed".
  let armedAt: number | null = null;

  return {
    press(key, now) {
      if (key === LEADER_KEY) {
        armedAt = now;
        return null;
      }

      const armed = armedAt !== null && now - armedAt <= timeoutMs;
      // Any non-leader key disarms the sequence, whether or not it completes one.
      armedAt = null;

      if (armed && Object.prototype.hasOwnProperty.call(map, key)) {
        return map[key];
      }
      return null;
    },
  };
}
